import type { RootDispatch, RootState } from '../../configuration/setup/store';
import { updateStateOfCharge } from '../../store/charging/chargingSlice';
import { EngineType, facadeApiV3, type TourResponse } from '../../store/facade/facadeApiV3';
import {
    updateEnergyCosts,
    updateRouteConstraints,
    updateRouteDeparture,
    updateVehicleCosts,
} from '../../store/route/routeSlice';
import { selectRoute } from '../../store/route/routeSelectors';
import { captureSettingsSnapshot } from '../../store/tour/settingsSnapshot';
import { selectCurrentTour } from '../../store/tour/tourSelectors';
import { saveSettingsSnapshot } from '../../store/tour/tourSlice';
import { selectProfileType, selectVehicleProfile } from '../../store/vehicleProfile/vehicleProfileSelectors';
import {
    changeAverageOutdoorTemperature,
    changeBatteryPacksValue,
    changeCargoWeight,
    changeHazardClassification,
    changeIsEV,
    changePlugType,
    changeProfileDetails,
    changeTunnelCategory,
    changeVehicleType,
} from '../../store/vehicleProfile/vehicleProfileSlice';
import { updateItineraryWaypoints } from '../search/updateItineraryWaypoint';
import { mapEvSettingsToStateOfCharge, mapTourToRouteState, mapTourToVehicleProfileFields } from './tourReverseMapper';

export const prefillTourSettings = (dispatch: RootDispatch, getState: () => RootState): void => {
    const state = getState();
    const tour = selectCurrentTour(state);
    if (!tour) {
        return;
    }

    const route = mapTourToRouteState(tour, selectRoute(state));
    dispatch(updateRouteDeparture(route.departure));
    dispatch(updateRouteConstraints(route.constraints));
    dispatch(updateVehicleCosts(route.vehicleCosts));
    dispatch(updateEnergyCosts(route.energyCosts));

    dispatch(changeVehicleType(tour.vehicle.vehicleProfileId ?? ''));
    dispatch(changeIsEV(tour.vehicle.engineType === EngineType.Electric));

    dispatch(updateStateOfCharge(mapEvSettingsToStateOfCharge(tour.vehicle.evSettings)));

    dispatch(updateItineraryWaypoints); // existing thunk — Requirement 8 criterion 1
};

export const reapplyVehicleProfileTourFields = async (
    dispatch: RootDispatch,
    getState: () => RootState,
    tour: TourResponse
): Promise<void> => {
    const profileType = selectProfileType(getState());
    if (!profileType) {
        return;
    }

    const { data: profileDetails } = await dispatch(
        facadeApiV3.endpoints.getVehicleProfileDetails.initiate({ id: profileType })
    );
    if (profileDetails) {
        dispatch(changeProfileDetails(profileDetails));
    }

    const fields = mapTourToVehicleProfileFields(tour, selectVehicleProfile(getState()));
    dispatch(changeCargoWeight(fields.cargoWeight));
    dispatch(changeAverageOutdoorTemperature(fields.averageOutdoorTemperature));
    dispatch(changeHazardClassification(fields.hazardClassification));
    dispatch(changeTunnelCategory(fields.tunnelCategory));
    dispatch(changePlugType({ value: fields.plugType, isEditable: true }));
    if (fields.batteryPacks !== undefined) {
        dispatch(changeBatteryPacksValue(fields.batteryPacks));
    }

    dispatch(saveSettingsSnapshot(captureSettingsSnapshot(getState())));
};
